import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { LOGO_URL } from "../lib/config";
import { useAuth } from "../context/AuthContext";

export const AuthPage = ({ isLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [accountType, setAccountType] = useState("individual");
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(name, email, password, city, state, zipCode, accountType, accountType === "business" ? companyName : null);
        toast.success("Account created successfully!");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="DigitalMe"
            className="w-16 h-16 rounded-2xl mx-auto mb-4 cursor-pointer object-cover"
            onClick={() => navigate("/")}
          />
          <h1 className="text-2xl font-bold text-white">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-gray-400 mt-2">
            {isLogin ? "Sign in to access your data vault" : "Start controlling your personal data"}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20"
          data-testid={isLogin ? "login-form" : "register-form"}
        >
          {!isLogin && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Account Type</label>
              <div className="grid grid-cols-2 gap-3" data-testid="account-type-selector">
                <button
                  type="button"
                  onClick={() => setAccountType("individual")}
                  className={`px-4 py-3 rounded-xl border text-sm font-medium transition ${accountType === "individual" ? "bg-gradient-to-r from-purple-600 to-cyan-500 text-white border-transparent" : "bg-white/10 text-gray-300 border-purple-500/30 hover:bg-white/20"}`}
                  data-testid="account-type-individual"
                >
                  Individual
                  <span className="block text-xs font-normal opacity-80 mt-0.5">Own & sell your data</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType("business")}
                  className={`px-4 py-3 rounded-xl border text-sm font-medium transition ${accountType === "business" ? "bg-gradient-to-r from-purple-600 to-cyan-500 text-white border-transparent" : "bg-white/10 text-gray-300 border-purple-500/30 hover:bg-white/20"}`}
                  data-testid="account-type-business"
                >
                  Business
                  <span className="block text-xs font-normal opacity-80 mt-0.5">Buy consented data</span>
                </button>
              </div>
            </div>
          )}
          {!isLogin && accountType === "business" && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                placeholder="Acme Data Corp"
                required
                data-testid="company-name-input"
              />
            </div>
          )}
          {!isLogin && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">{accountType === "business" ? "Contact Name" : "Full Name"}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                placeholder="John Doe"
                required
                data-testid="name-input"
              />
            </div>
          )}
          {!isLogin && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                placeholder="New York"
                data-testid="city-input"
              />
            </div>
          )}
          {!isLogin && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">State</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                  placeholder="NY"
                  data-testid="state-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Zip Code</label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                  placeholder="10001"
                  data-testid="zipcode-input"
                />
              </div>
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
              placeholder="you@example.com"
              required
              data-testid="email-input"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition pr-12 text-white placeholder-gray-500"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 via-violet-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isLogin ? (
              "Sign In"
            ) : (
              "Create Account"
            )}
          </button>
          <p className="text-center mt-6 text-gray-400">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => navigate(isLogin ? "/register" : "/login")}
              className="text-cyan-400 font-medium hover:underline"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

// Install Banner Component
